/**
 * AddFoodSheet — web port of mobile components/diary/AddFoodSheet.tsx.
 *
 * "Add Food" with two large tiles (Scan — photo, label or barcode; Say or
 * Type) over three small ones (Saved, Recipes, Manual), each with its clay
 * quick-add art. A bottom sheet at phone width, a centered card on wider
 * screens. Web is Pro-only, so the free-analyses line never shows.
 *
 * Shared contract (shell + diary import it): keep this signature stable.
 * z-30: the shell's floating tab bar (z-40) stays above the sheet, as in the
 * app, so its centre button can close it.
 */

import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { QUICK_ADD_IMAGES } from '@/constants/meal-icons'
import { useColorScheme } from '@/hooks/use-color-scheme'
import { ManualEntryModal } from '@/components/diary/ManualEntryModal'
import type { MealTime } from '@/types'

type OptionId = 'scan' | 'quickLog' | 'savedFoods' | 'recipes' | 'manual'

const TOP_OPTIONS: { id: OptionId; image: string }[] = [
  { id: 'scan', image: QUICK_ADD_IMAGES.scanMeal },
  { id: 'quickLog', image: QUICK_ADD_IMAGES.voiceText },
]
const BOTTOM_OPTIONS: { id: OptionId; image: string }[] = [
  { id: 'savedFoods', image: QUICK_ADD_IMAGES.savedFoods },
  { id: 'recipes', image: QUICK_ADD_IMAGES.recipes },
  { id: 'manual', image: QUICK_ADD_IMAGES.manual },
]

function OptionCard({
  id,
  image,
  index,
  wide,
  onPress,
}: {
  id: OptionId
  image: string
  index: number
  wide?: boolean
  onPress: () => void
}) {
  const { t } = useTranslation()
  const isDark = useColorScheme() === 'dark'
  return (
    <div
      className={(wide ? 'flex-1' : 'w-[30%]') + ' animate-slide-up'}
      style={{ animationDelay: `${index * 80}ms`, animationFillMode: 'both' }}
    >
      <button
        type="button"
        onClick={onPress}
        className={
          'flex h-full min-h-[130px] w-full flex-col items-center overflow-hidden rounded-xl bg-surface-inner px-sm py-lg transition-transform duration-150 active:scale-[0.96] ' +
          (isDark ? '' : 'border-[0.5px] border-border shadow-[0_4px_12px_rgba(0,0,0,0.08)]')
        }
      >
        <img src={image} alt="" aria-hidden className="mb-sm h-12 w-12 object-contain" />
        <span className="mb-xs text-center text-[16px] font-semibold leading-[normal] text-text">
          {t(`diary.addFood.options.${id}.title`)}
        </span>
        <span className="text-center text-[12px] leading-[normal] text-text-secondary opacity-80">
          {t(`diary.addFood.options.${id}.subtitle`)}
        </span>
      </button>
    </div>
  )
}

export function AddFoodSheet({
  open,
  onClose,
  mealType,
}: {
  open: boolean
  onClose: () => void
  mealType?: 'breakfast' | 'lunch' | 'dinner' | 'snack'
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isDark = useColorScheme() === 'dark'
  const [manualOpen, setManualOpen] = useState(false)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const meal = mealType ? `&meal=${mealType}` : ''
  const handle = (id: OptionId) => {
    switch (id) {
      case 'scan':
        navigate(`/log?mode=scan${meal}`)
        break
      case 'quickLog':
        navigate(`/log?mode=describe${meal}`)
        break
      case 'savedFoods':
        navigate(mealType ? `/saved-foods?meal=${mealType}` : '/saved-foods')
        break
      case 'recipes':
        navigate('/recipes')
        break
      case 'manual':
        setManualOpen(true)
        break
    }
    onClose()
  }

  return (
    <>
      {open
        ? createPortal(
            <div className="fixed inset-0 z-30 flex items-end justify-center md:items-center md:p-lg" role="dialog" aria-modal="true" aria-label={t('diary.addFood.title')}>
              <div className="absolute inset-0 animate-fade-in bg-black/60" onClick={onClose} />
              <div
                className={
                  'relative flex h-[70dvh] w-full animate-slide-up flex-col leading-[normal] rounded-t-[24px] border-t bg-bg px-lg shadow-[0_-4px_20px_rgba(0,0,0,0.15)] md:h-auto md:max-w-content md:rounded-xxl md:border md:pb-xl ' +
                  (isDark ? 'border-white/10' : 'border-black/[0.08]')
                }
              >
                {/* Handle */}
                <div className="flex justify-center pt-sm md:invisible">
                  <span className="mt-sm h-1 w-10 rounded-sm bg-border" />
                </div>

                {/* Header */}
                <div className="flex w-full animate-fade-in flex-col items-center pb-xl pt-md">
                  <h2 className="text-[24px] font-bold leading-[normal] tracking-[-0.5px] text-text">{t('diary.addFood.title')}</h2>
                  {mealType ? (
                    <p className="mt-xs text-[15px] leading-[normal] text-text-secondary">
                      {t('diary.addFood.subtitle', { meal: t(`diary.meals.${mealType}`) })}
                    </p>
                  ) : null}
                </div>

                {/* Top row: Scan + Say or Type */}
                <div className="mb-md flex w-full gap-md">
                  {TOP_OPTIONS.map((o, i) => (
                    <OptionCard key={o.id} id={o.id} image={o.image} index={i} wide onPress={() => handle(o.id)} />
                  ))}
                </div>

                {/* Bottom row: Saved, Recipes, Manual */}
                <div className="flex w-full items-stretch justify-between gap-md">
                  {BOTTOM_OPTIONS.map((o, i) => (
                    <OptionCard key={o.id} id={o.id} image={o.image} index={i + 2} onPress={() => handle(o.id)} />
                  ))}
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}

      <ManualEntryModal open={manualOpen} onClose={() => setManualOpen(false)} defaultMeal={mealType} />
    </>
  )
}

export default AddFoodSheet
